/*
 * Copyright (c) 2018-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import React from 'react';
import {Route, useHistory, Switch} from 'react-router-dom';
import {OktaAuth, toRelativeUrl} from '@okta/okta-auth-js';
import {Security, SecureRoute, LoginCallback} from '@okta/okta-react';
import {Provider} from 'react-redux';
import store from './redux/store';
import Home from './Home';
import CustomLoginComponent from './Login';
import CorsErrorModal from './CorsErrorModal';
import AuthRequiredModal from './AuthRequiredModal';

import Dashboard from './pages/dashboard';
import Coursesmanagement from './pages/coursesManagement';
import ContentManagement from './pages/contentManagement';
import AudienceManagement from './pages/audienceManagement';
import learningPathModal from './pages/learningPath';
import Layout from './components/Layout';
import AddNewImage from './pages/addNewImage';
import EditImage from './pages/editModal';
import PageNotFound from './pages/pageNotFound';
import AudienceManageCourses from './pages/audienceManageCourses';
import LearningPathManageCourse from './pages/learningPathManageCourse';
import CreateLearningPath from './pages/createLearningPath';
import CategorySkillsTagsManagement from './pages/categorySkillsTagsManagement';
import CreateCourseWizard from './pages/newCourseWizard';
import {getCurrentRegionOIDCConfigs} from './components/utils/APIsUtils/httpsUtils';
const currentRegionOIDCConfigs = getCurrentRegionOIDCConfigs();
const oktaAuth = new OktaAuth(currentRegionOIDCConfigs);

import './styles/styles.scss';

const App = () => {
  const [corsErrorModalOpen, setCorsErrorModalOpen] = React.useState(false);
  const [authRequiredModalOpen, setAuthRequiredModalOpen] = React.useState(false);

  const history = useHistory(); // example from react-router

  const triggerLogin = () => {
    // Redirect to the /login page that has a CustomLoginComponent
    history.push('/login');
  };

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  const customAuthHandler = async () => {
    const previousAuthState = oktaAuth.authStateManager.getPreviousAuthState();
    if (!previousAuthState || !previousAuthState.isAuthenticated) {
      // App initialization stage
      triggerLogin();
    } else {
      // Ask the user to trigger the login process during token autoRenew process
      setAuthRequiredModalOpen(true);
    }
  };

  const onAuthResume = async () => {
    history.push('/login');
  };

  return (
    <>
      <Provider store={store}>
        <Layout>
          <Security
            oktaAuth={oktaAuth}
            onAuthRequired={customAuthHandler}
            restoreOriginalUri={restoreOriginalUri}
          >
            <CorsErrorModal {...{corsErrorModalOpen, setCorsErrorModalOpen}} />
            <AuthRequiredModal
              {...{
                authRequiredModalOpen,
                setAuthRequiredModalOpen,
                triggerLogin,
              }}
            />
            <Switch>
              <SecureRoute path="/Dashboard" exact component={Dashboard} />
              <SecureRoute path="/Coursesmanagement" exact component={Coursesmanagement} />
              <SecureRoute path="/Contentmanagement" exact component={ContentManagement} />
              <SecureRoute path="/UserManagement" exact component={AudienceManagement} />
              <SecureRoute path="/LearningPathModal" exact component={learningPathModal} />
              <SecureRoute path="/add-new-image" exact component={AddNewImage} />
              <SecureRoute path="/edit-modal" exact component={EditImage} />
              <SecureRoute path="/ManageCourses" exact component={AudienceManageCourses} />
              <SecureRoute path="/ManageLearningPath" expact component={LearningPathManageCourse} />
              <SecureRoute path="/newLearningPath" exact component={CreateLearningPath} />
              <SecureRoute path="/create-course-wizard" exact component={CreateCourseWizard} />
              <SecureRoute
                path="/CategorySkillsTagsmanagement"
                exact
                component={CategorySkillsTagsManagement}
              />
              <Route path="/" exact component={Home} />
              <Route
                path="/login/callback"
                render={(props) => <LoginCallback {...props} onAuthResume={onAuthResume} />}
              />
              <Route
                path="/login"
                render={() => <CustomLoginComponent {...{setCorsErrorModalOpen}} />}
              />
              <Route path="*" component={PageNotFound} />
            </Switch>
          </Security>
        </Layout>
      </Provider>
      <link
        rel="stylesheet"
        href="https://stackpath.bootstrapcdn.com/bootstrap/4.1.0/css/bootstrap.min.css"
        crossOrigin="anonymous"
      />
    </>
  );
};

export default App;
